import { useCallback, useEffect, useRef, useState } from "react";

import type { FieldPilotAnnotationMode } from "@/features/field-pilot/label-utils";

export type FieldPilotColorizeBy = "status" | "tag";

export interface FieldPilotDisplayOptions {
  colorizeBy: FieldPilotColorizeBy;
  showMeasurementValues: boolean;
  viewportAnnotation: FieldPilotAnnotationMode;
}

export const DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS: FieldPilotDisplayOptions = {
  colorizeBy: "status",
  showMeasurementValues: true,
  viewportAnnotation: "off",
};

export const FIELD_PILOT_DISPLAY_OPTIONS_STORAGE_PREFIX = "field-pilot-display-options:v1";

const COLORIZE_BY_VALUES = new Set<FieldPilotColorizeBy>(["status", "tag"]);
const ANNOTATION_MODE_VALUES = new Set<FieldPilotAnnotationMode>([
  "off",
  "next",
  "current",
  "all",
]);

export function fieldPilotDisplayOptionsStorageKey(jobId: string) {
  return `${FIELD_PILOT_DISPLAY_OPTIONS_STORAGE_PREFIX}:${jobId}`;
}

function isColorizeBy(value: unknown): value is FieldPilotColorizeBy {
  return typeof value === "string" && COLORIZE_BY_VALUES.has(value as FieldPilotColorizeBy);
}

function isAnnotationMode(value: unknown): value is FieldPilotAnnotationMode {
  return (
    typeof value === "string" &&
    ANNOTATION_MODE_VALUES.has(value as FieldPilotAnnotationMode)
  );
}

export function parseFieldPilotDisplayOptions(raw: string | null): FieldPilotDisplayOptions {
  if (!raw) {
    return { ...DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return { ...DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS };
    }
    const record = parsed as Record<string, unknown>;
    return {
      colorizeBy: isColorizeBy(record.colorizeBy)
        ? record.colorizeBy
        : DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS.colorizeBy,
      showMeasurementValues:
        typeof record.showMeasurementValues === "boolean"
          ? record.showMeasurementValues
          : DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS.showMeasurementValues,
      viewportAnnotation: isAnnotationMode(record.viewportAnnotation)
        ? record.viewportAnnotation
        : DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS.viewportAnnotation,
    };
  } catch {
    return { ...DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS };
  }
}

export function readFieldPilotDisplayOptions(jobId: string): FieldPilotDisplayOptions {
  if (!jobId || typeof window === "undefined") {
    return { ...DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS };
  }
  try {
    return parseFieldPilotDisplayOptions(
      window.localStorage.getItem(fieldPilotDisplayOptionsStorageKey(jobId))
    );
  } catch {
    return { ...DEFAULT_FIELD_PILOT_DISPLAY_OPTIONS };
  }
}

export function writeFieldPilotDisplayOptions(
  jobId: string,
  options: FieldPilotDisplayOptions
) {
  if (!jobId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      fieldPilotDisplayOptionsStorageKey(jobId),
      JSON.stringify(options)
    );
  } catch {
    // Ignore quota / private-mode failures; in-memory state still works.
  }
}

export interface UseFieldPilotDisplayOptionsArgs {
  jobId: string;
}

export function useFieldPilotDisplayOptions({ jobId }: UseFieldPilotDisplayOptionsArgs) {
  const [options, setOptions] = useState<FieldPilotDisplayOptions>(() =>
    readFieldPilotDisplayOptions(jobId)
  );
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const next = readFieldPilotDisplayOptions(jobId);
    optionsRef.current = next;
    setOptions(next);
  }, [jobId]);

  const persist = useCallback(
    (next: FieldPilotDisplayOptions) => {
      optionsRef.current = next;
      setOptions(next);
      writeFieldPilotDisplayOptions(jobId, next);
    },
    [jobId]
  );

  const setColorizeBy = useCallback(
    (colorizeBy: FieldPilotColorizeBy) => {
      const current = optionsRef.current;
      if (current.colorizeBy === colorizeBy) return;
      persist({ ...current, colorizeBy });
    },
    [persist]
  );

  const setShowMeasurementValues = useCallback(
    (showMeasurementValues: boolean) => {
      const current = optionsRef.current;
      if (current.showMeasurementValues === showMeasurementValues) return;
      persist({ ...current, showMeasurementValues });
    },
    [persist]
  );

  const setViewportAnnotation = useCallback(
    (viewportAnnotation: FieldPilotAnnotationMode) => {
      const current = optionsRef.current;
      if (current.viewportAnnotation === viewportAnnotation) return;
      persist({ ...current, viewportAnnotation });
    },
    [persist]
  );

  return {
    colorizeBy: options.colorizeBy,
    showMeasurementValues: options.showMeasurementValues,
    viewportAnnotation: options.viewportAnnotation,
    setColorizeBy,
    setShowMeasurementValues,
    setViewportAnnotation,
  };
}
